import { Die, YatzyScores } from '../types/game';
import { calculatePossibleScore, scoreCategories } from './gameLogic';

// Definiera strategy modes för olika spelstrategier
export enum StrategyMode {
  BALANCED = 'balanced',
  AGGRESSIVE = 'aggressive',
  CONSERVATIVE = 'conservative',
  BONUS_FOCUSED = 'bonus_focused',
  YATZY_HUNTING = 'yatzy_hunting'
}

export interface StrategyResult {
  category: string | null;
  expectedValue: number;
  reasoning: string;
  confidence: number;
  diceToKeep: number[] | null;
  alternativeMove: { reasoning: string } | null;
}

const UPPER_KEYS: (keyof YatzyScores)[] = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'];
const FACE_FOR_KEY: Record<string, number> = { ones: 1, twos: 2, threes: 3, fours: 4, fives: 5, sixes: 6 };

function countValues(values: number[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const v of values) counts[v] = (counts[v] || 0) + 1;
  return counts;
}

/** Bonus gap: how many more points needed for upper section >= 63 */
function bonusGap(scores: YatzyScores, upperSectionScore: number): number {
  return Math.max(0, 63 - upperSectionScore);
}

/** Upper section remaining capacity (unfilled categories × 3 × face) */
function upperRemaining(scores: YatzyScores): number {
  return UPPER_KEYS.reduce((sum, k) => {
    if ((scores as any)[k] === null) {
      return sum + FACE_FOR_KEY[k] * 3; // 3 of each face is "on track" for bonus
    }
    return sum;
  }, 0);
}

export class StrategyAdvisor {
  /**
   * Analysera spelpositionen och ge strategirekommendation.
   */
  static analyzePosition(
    dice: Die[],
    scores: YatzyScores,
    rollsLeft: number,
    upperSectionScore: number,
    strategyMode: StrategyMode = StrategyMode.BALANCED
  ): StrategyResult {
    if (!dice || dice.length === 0 || dice.every(d => d.value === 0)) {
      return {
        category: null,
        expectedValue: 0,
        reasoning: 'Kasta tärningarna för att börja din tur.',
        confidence: 0,
        diceToKeep: null,
        alternativeMove: null,
      };
    }

    if (rollsLeft === 0) {
      return this.recommendCategory(dice, scores, upperSectionScore, strategyMode);
    }

    return this.recommendKeep(dice, scores, rollsLeft, upperSectionScore, strategyMode);
  }

  // ────── Category Selection (rollsLeft === 0) ──────

  private static recommendCategory(
    dice: Die[],
    scores: YatzyScores,
    upperSectionScore: number,
    mode: StrategyMode
  ): StrategyResult {
    const sc = scores as any;

    // Score every unfilled category
    type Candidate = { key: string; score: number; priority: number; label: string };
    const candidates: Candidate[] = [];

    for (const cat of scoreCategories) {
      if (sc[cat.key] !== null) continue; // already filled
      const score = calculatePossibleScore(dice, cat.key);
      let priority = score; // base priority = raw score

      // Mode adjustments
      priority = this.adjustPriority(cat.key, score, priority, mode, upperSectionScore, scores);

      candidates.push({ key: cat.key, score, priority, label: cat.name });
    }

    if (candidates.length === 0) {
      return { category: null, expectedValue: 0, reasoning: 'Alla kategorier fyllda.', confidence: 0, diceToKeep: null, alternativeMove: null };
    }

    // Sort by adjusted priority desc
    candidates.sort((a, b) => b.priority - a.priority);

    const best = candidates[0];
    const alt = candidates.length > 1 ? candidates[1] : null;

    // Confidence: high if big gap between best and second
    let confidence = 70;
    if (best.score >= 50) confidence = 99;               // Yatzy
    else if (best.score > 0 && (!alt || best.priority > alt.priority * 1.5)) confidence = 90;
    else if (best.score === 0) confidence = 25;           // striking

    const reasoning = best.score > 0
      ? `${best.label} ger ${best.score} poäng${this.modeHint(mode)}`
      : `Stryk ${best.label} (0 poäng) — inget bättre alternativ`;

    return {
      category: best.key,
      expectedValue: best.score,
      reasoning,
      confidence,
      diceToKeep: null,
      alternativeMove: alt
        ? { reasoning: `${alt.label}: ${alt.score} poäng` }
        : null,
    };
  }

  private static adjustPriority(
    key: string,
    score: number,
    basePriority: number,
    mode: StrategyMode,
    upperSectionScore: number,
    scores: YatzyScores
  ): number {
    let p = basePriority;
    const gap = bonusGap(scores, upperSectionScore);
    const remaining = upperRemaining(scores);
    const isUpper = UPPER_KEYS.includes(key as keyof YatzyScores);
    const face = FACE_FOR_KEY[key] || 0;

    switch (mode) {
      case StrategyMode.AGGRESSIVE:
        // Prioritera yatzy + fyrtal + kåk
        if (key === 'yatzy' && score > 0) p += 30;
        if (key === 'fourOfAKind' && score > 0) p += 15;
        if (key === 'fullHouse' && score > 0) p += 10;
        // Straffa säkra men låga val
        if (isUpper && score <= face * 2) p -= 5;
        break;

      case StrategyMode.CONSERVATIVE:
        // Prioritera upper section bonus progress
        if (isUpper && score > 0) {
          const onTrack = score >= face * 3;
          p += onTrack ? 15 : 5;
        }
        // Chance som säkert val
        if (key === 'chance') p += 8;
        // Straffa riskabla val (yatzy = 0 om misslyckas)
        if (key === 'yatzy' && score === 0) p -= 10;
        break;

      case StrategyMode.BONUS_FOCUSED:
        // Starkt fokus på att nå 63 i övre sektionen
        if (isUpper && score > 0) {
          const surplus = score - face * 3;
          p += gap > 0 ? (surplus >= 0 ? 25 : 10) : 5;
        }
        // Straffa nedre sektionen om bonus inte säkrad
        if (!isUpper && gap > 0 && remaining >= gap) p -= 10;
        break;

      case StrategyMode.YATZY_HUNTING:
        if (key === 'yatzy' && score > 0) p += 50;
        if (key === 'fourOfAKind' && score > 0) p += 10;
        if (key === 'threeOfAKind' && score > 0) p += 5;
        break;

      case StrategyMode.BALANCED:
      default:
        // Bonus awareness: mild boost to upper section when gap is tight
        if (isUpper && score > 0 && gap > 0 && gap <= remaining) {
          p += score >= face * 3 ? 10 : 3;
        }
        break;
    }

    return p;
  }

  // ────── Dice Keep Recommendation (rollsLeft > 0) ──────

  private static recommendKeep(
    dice: Die[],
    scores: YatzyScores,
    rollsLeft: number,
    upperSectionScore: number,
    mode: StrategyMode
  ): StrategyResult {
    const values = dice.map(d => d.value);
    const counts = countValues(values);
    const sc = scores as any;
    const gap = bonusGap(scores, upperSectionScore);

    // 1) Already have Yatzy (5-of-a-kind)?
    const fiveOfKind = Object.entries(counts).find(([_, c]) => c >= 5);
    if (fiveOfKind) {
      return {
        category: null,
        diceToKeep: dice.map((_, i) => i), // keep all
        expectedValue: 50,
        reasoning: 'YATZY! Behåll alla tärningar!',
        confidence: 100,
        alternativeMove: null,
      };
    }

    // 2) Four of a kind — keep them, try for Yatzy
    const fourEntry = Object.entries(counts).find(([_, c]) => c >= 4);
    if (fourEntry) {
      const fv = parseInt(fourEntry[0]);
      const keep = values.map((v, i) => v === fv ? i : -1).filter(i => i >= 0);

      if (mode === StrategyMode.YATZY_HUNTING || mode === StrategyMode.AGGRESSIVE) {
        return {
          category: null,
          diceToKeep: keep,
          expectedValue: fv * 4,
          reasoning: `Fyrtal i ${fv}or — satsa på Yatzy! Behåll alla ${fv}or och kasta om resten.`,
          confidence: 85,
          alternativeMove: sc.fourOfAKind === null
            ? { reasoning: `Alternativt, ta fyrtal nu (${fv * 4} poäng)` }
            : null,
        };
      }
      // Balanced/conservative: still keep, but note option to score immediately
      return {
        category: null,
        diceToKeep: keep,
        expectedValue: fv * 4,
        reasoning: `Fyrtal i ${fv}or — behåll och försök förbättra.`,
        confidence: 80,
        alternativeMove: rollsLeft === 1 && sc.fourOfAKind === null
          ? { reasoning: `Säkra fyrtal direkt (${fv * 4} poäng)` }
          : null,
      };
    }

    // 3) Three of a kind — keep and build
    const threeEntry = Object.entries(counts).find(([_, c]) => c >= 3);
    if (threeEntry) {
      const fv = parseInt(threeEntry[0]);
      const keep = values.map((v, i) => v === fv ? i : -1).filter(i => i >= 0);

      // Also check for full house potential (need a pair among remaining dice)
      const remaining = values.filter(v => v !== fv);
      const remainingCounts = countValues(remaining);
      const hasPairLeft = Object.values(remainingCounts).some(c => c >= 2);

      if (hasPairLeft && sc.fullHouse === null) {
        // Keep the pair too for full house
        const pairVal = parseInt(Object.entries(remainingCounts).find(([_, c]) => c >= 2)![0]);
        const fullKeep = values.map((v, i) => (v === fv || v === pairVal) ? i : -1).filter(i => i >= 0);
        return {
          category: null,
          diceToKeep: fullKeep,
          expectedValue: fv * 3 + pairVal * 2,
          reasoning: `Kåk möjlig — triss i ${fv}or + par i ${pairVal}or. Behåll alla!`,
          confidence: 90,
          alternativeMove: { reasoning: `Alternativt, kasta om paret och sikta på fyrtal ${fv}or` },
        };
      }

      return {
        category: null,
        diceToKeep: keep,
        expectedValue: fv * 3,
        reasoning: `Triss i ${fv}or — behåll och sikta på fyrtal eller kåk.`,
        confidence: 75,
        alternativeMove: null,
      };
    }

    // 4) Two pairs — evaluate
    const pairEntries = Object.entries(counts)
      .filter(([_, c]) => c >= 2)
      .sort(([a], [b]) => parseInt(b) - parseInt(a));

    if (pairEntries.length >= 2) {
      const [pv1] = pairEntries[0];
      const [pv2] = pairEntries[1];
      const keepIndices: number[] = [];
      const used = new Set<number>();
      for (const pv of [parseInt(pv1), parseInt(pv2)]) {
        let kept = 0;
        for (let i = 0; i < values.length; i++) {
          if (!used.has(i) && values[i] === pv && kept < 2) { keepIndices.push(i); used.add(i); kept++; }
        }
      }
      return {
        category: null,
        diceToKeep: keepIndices,
        expectedValue: parseInt(pv1) * 2 + parseInt(pv2) * 2,
        reasoning: `Två par (${pv1}or och ${pv2}or) — sikta på kåk med sista tärningen.`,
        confidence: 70,
        alternativeMove: { reasoning: `Alternativt, behåll bara det högsta paret (${pv1}or) och sikta på triss` },
      };
    }

    // 5) Single pair
    if (pairEntries.length === 1) {
      const fv = parseInt(pairEntries[0][0]);
      const keep = values.map((v, i) => v === fv ? i : -1).filter(i => i >= 0);

      // Bonus-focused: if pair aligns with upper section need, boost
      if ((mode === StrategyMode.CONSERVATIVE || mode === StrategyMode.BONUS_FOCUSED) && gap > 0) {
        const upperKey = UPPER_KEYS.find(k => FACE_FOR_KEY[k] === fv);
        if (upperKey && sc[upperKey] === null) {
          return {
            category: null,
            diceToKeep: keep,
            expectedValue: fv * 2,
            reasoning: `Par i ${fv}or — behåll för att bygga mot övre sektionens bonus (${gap} poäng kvar till 63).`,
            confidence: 65,
            alternativeMove: { reasoning: 'Alternativt, kasta om allt och satsa på stege' },
          };
        }
      }

      return {
        category: null,
        diceToKeep: keep,
        expectedValue: fv * 2,
        reasoning: `Par i ${fv}or — behåll och försök bygga triss eller kåk.`,
        confidence: 60,
        alternativeMove: null,
      };
    }

    // 6) Straight potential (4 in sequence)
    const sorted = Array.from(new Set(values)).sort((a, b) => a - b);
    if (sorted.length >= 4) {
      // Find longest run
      let bestRun: number[] = [sorted[0]];
      let currentRun: number[] = [sorted[0]];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === sorted[i - 1] + 1) {
          currentRun.push(sorted[i]);
          if (currentRun.length > bestRun.length) bestRun = [...currentRun];
        } else {
          currentRun = [sorted[i]];
        }
      }

      if (bestRun.length >= 4) {
        const runSet = new Set(bestRun);
        const keep = values.map((v, i) => runSet.has(v) ? i : -1).filter(i => i >= 0);
        // Deduplicate: only keep first occurrence of each value
        const kept = new Set<number>();
        const uniqueKeep = keep.filter(i => {
          if (kept.has(values[i])) return false;
          kept.add(values[i]);
          return true;
        });

        const target = bestRun.includes(1) ? 'liten stege (15p)' : 'stor stege (20p)';
        return {
          category: null,
          diceToKeep: uniqueKeep,
          expectedValue: bestRun.includes(1) ? 15 : 20,
          reasoning: `${bestRun.length} i rad (${bestRun.join('-')}) — sikta på ${target}!`,
          confidence: bestRun.length === 4 ? 65 : 85,
          alternativeMove: null,
        };
      }
    }

    // 7) No pattern — keep highest die(s)
    const maxVal = Math.max(...values);
    const keep = [values.indexOf(maxVal)];

    // Aggressive: keep nothing, reroll everything
    if (mode === StrategyMode.AGGRESSIVE) {
      return {
        category: null,
        diceToKeep: [],
        expectedValue: 0,
        reasoning: 'Inga mönster — kasta om alla tärningar!',
        confidence: 30,
        alternativeMove: { reasoning: `Alternativt, behåll ${maxVal}:an` },
      };
    }

    return {
      category: null,
      diceToKeep: keep,
      expectedValue: maxVal,
      reasoning: `Inga tydliga mönster — behåll högsta (${maxVal}) och kasta om resten.`,
      confidence: 35,
      alternativeMove: null,
    };
  }

  // ────── Helpers ──────

  private static modeHint(mode: StrategyMode): string {
    switch (mode) {
      case StrategyMode.AGGRESSIVE: return ' (aggressivt läge)';
      case StrategyMode.CONSERVATIVE: return ' (säkert läge)';
      case StrategyMode.BONUS_FOCUSED: return ' (bonusfokus)';
      case StrategyMode.YATZY_HUNTING: return ' (yatzy-jakt)';
      default: return '';
    }
  }
}
